import {ReactNode, createContext, useCallback, useContext, useEffect} from 'react';
import {useNavigate, useParams} from 'react-router';

import {productT, productUpdateT, productWithBrandAndWeedmapT} from '@/lib/types/product.types';

import {useItemQuery} from '@/hooks/useItemQuery';

import {useAuth} from '@/providers/components/authProvider';
import {useProducts} from '@/providers/components/productsProviders';

/* ******************************************************************
 * ProductContext
 ****************************************************************** */
type ProductContextT = {
  // Properties
  product: productWithBrandAndWeedmapT | undefined;
  isLoading: boolean;
  error: Error | null;
  // Functions
  refreshProduct: () => Promise<void>;
  updateProduct: (product: productUpdateT) => Promise<productT>;
  deleteProduct: (id: number) => Promise<void>;
};

const ProductContext = createContext({} as ProductContextT);

/* ******************************************************************
 * ProductProvider
 ****************************************************************** */
type ProductProviderPropsT = {
  children: ReactNode;
};

/**
 * @param {ProductProviderPropsT} props - The component props.
 * @returns {Context<ProductContextT>} - The Product provider component.
 */
export const ProductProvider = ({children}: ProductProviderPropsT) => {
  const navigate = useNavigate();
  const {isAdministrator} = useAuth();
  const {id: ProductIdFromParams} = useParams();
  const {updateData: updateProductMutation, deleteData: deleteProductMutation} = useProducts();

  // Constants
  const productId = Number(ProductIdFromParams);

  // TanStack Query hooks
  const {
    data: fetchedProduct,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useItemQuery({table: 'Product', id: productId, columns: ['*', ['Brand', ['*']], ['Weedmap_Product', ['*']]]});

  /*******************************************************************
   * Functions
   ********************************************************************/
  /********************************************************************
   * Gets a Product records from the database.
   * @returns {Promise<productT | null>} - The Product object.
   *******************************************************************/
  const getProduct = useCallback(async () => {
    if (!isAdministrator) throw new Error('Error getting Products');
    try {
      const {data} = await refetch();
      const _Product = data ?? null;
      return _Product;
    } catch (error) {
      console.error('Error in getRetailer:', error);
      return undefined;
    }
  }, [isAdministrator, refetch]);

  /********************************************************************
   * Refreshes the Product data from the database.
   * @returns {Promise<void>}
   *******************************************************************/
  const refreshProduct = async () => {
    await getProduct();
  };

  /********************************************************************
   * Updates the Product in the database.
   * @param {productT} Product - The Product object to update.
   * @returns {Promise<productT>} - The updated Product object.
   *******************************************************************/
  const updateProduct = async (product: productUpdateT) => {
    if (!isAdministrator) throw new Error('Error updating Product');

    try {
      const updatedProduct = await updateProductMutation(product);
      return updatedProduct;
    } catch (error) {
      console.error('Error in updateRetailer:', error);
      throw error;
    }
  };

  /********************************************************************
   * Deletes the Product from the database.
   * @returns {Promise<void>}
   *******************************************************************/
  const deleteProduct = async () => {
    if (!isAdministrator) throw new Error('Error deleting Product');

    // We must have a Product to delete
    if (!fetchedProduct) throw new Error('Error deleting Product');

    try {
      await deleteProductMutation(fetchedProduct.product_id);
      navigate('/admin/Products');
    } catch (error) {
      console.error('Error in deleteProduct:', error);
      throw error;
    }
  };

  /*******************************************************************
   * Configuration
   ********************************************************************/
  useEffect(() => {
    // If there's an error getting the Product, navigate back to the Product list
    if (error || !productId) {
      if (error) console.error(error);
      if (!productId) navigate('/admin/products');
    }
  }, [error, navigate, productId, fetchedProduct]);

  return (
    <ProductContext.Provider
      value={{
        product: fetchedProduct,
        isLoading: isLoading || isFetching,
        error,
        refreshProduct,
        updateProduct,
        deleteProduct,
      }}
    >
      {children}
    </ProductContext.Provider>
  );
};

/* ******************************************************************
 * useProduct Hook
 ****************************************************************** */
export const useProduct = () => {
  const context = useContext(ProductContext);
  if (context && Object.keys(context).length === 0 && Object.getPrototypeOf(context) === Object.prototype) {
    throw new Error('useProduct must be used within a ProductProvider');
  }

  return context;
};
